import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getValidToken, refreshToken, isTokenExpired } from '../lib/auth';
import { getActiveCompanyId } from '../lib/activeCompany';

export interface AggregateOrderStats {
  total_orders: number;
  total_revenue: number;
  pending_orders: number;
  fulfilled_orders: number;
  ready_to_ship: number;
  to_prepare: number;
  with_tracking: number;
  today_orders: number;
  today_revenue: number;
  printed_today: number;
  avg_order_value: number;
  fulfillment_rate: number;
  primary_currency: string;
  last_order_date: string | null;
  oldest_order_date: string | null;
  calculated_at: string;
  is_cached: boolean;
  orders_with_missing_items: number;
}

interface UseOrderStatsOptions {
  enabled?: boolean;
  autoRefresh?: boolean;
  refreshInterval?: number;
  maxAge?: number;
}

interface UseOrderStatsReturn {
  stats: AggregateOrderStats | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  isCached: boolean;
  refresh: (force?: boolean) => Promise<void>;
}

const DEFAULT_STATS: AggregateOrderStats = {
  total_orders: 0,
  total_revenue: 0,
  pending_orders: 0,
  fulfilled_orders: 0,
  ready_to_ship: 0,
  to_prepare: 0,
  with_tracking: 0,
  today_orders: 0,
  today_revenue: 0,
  printed_today: 0,
  avg_order_value: 0,
  fulfillment_rate: 0,
  primary_currency: 'EUR',
  last_order_date: null,
  oldest_order_date: null,
  calculated_at: new Date().toISOString(),
  is_cached: false,
  orders_with_missing_items: 0,
};

export function useOrderStats(options: UseOrderStatsOptions = {}): UseOrderStatsReturn {
  const {
    enabled = true,
    autoRefresh = true,
    refreshInterval = 60000,
    maxAge = 60,
  } = options;

  const [stats, setStats] = useState<AggregateOrderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isCached, setIsCached] = useState(false);

  const channelRef = useRef<RealtimeChannel | null>(null);
  const intervalRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  const consecutiveAuthFailures = useRef(0);
  const statsRef = useRef<AggregateOrderStats | null>(null);
  const isFetchingRef = useRef(false);

  statsRef.current = stats;

  const getUserId = useCallback((): string | null => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return null;
      const user = JSON.parse(userStr);
      return user?.id || null;
    } catch {
      return null;
    }
  }, []);

  const getAdminId = useCallback((): string | null => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return null;
      const user = JSON.parse(userStr);
      // Prefer the token's company claim so the stats follow a company switch.
      return getActiveCompanyId() || user?.admin_id || user?.id || null;
    } catch {
      return null;
    }
  }, []);

  const isAuthenticated = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    const userId = getUserId();
    return Boolean(token && userId);
  }, [getUserId]);

  const getAuthHeaders = useCallback(async (): Promise<Record<string, string> | null> => {
    const token = await getValidToken();
    if (!token) return null;
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  const fetchStats = useCallback(async (force = false) => {
    if (isFetchingRef.current) return;
    if (!enabled) {
      setLoading(false);
      return;
    }

    isFetchingRef.current = true;
    const headers = await getAuthHeaders();
    if (!headers) {
      setLoading(false);
      setStats(DEFAULT_STATS);
      isFetchingRef.current = false;
      return;
    }

    try {
      const params = new URLSearchParams({
        maxAge: maxAge.toString(),
      });

      if (force) {
        params.set('refresh', 'true');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-order-stats?${params}`,
        { headers, signal: controller.signal }
      );
      clearTimeout(timeoutId);

      if (!mountedRef.current) return;

      if (response.status === 401) {
        consecutiveAuthFailures.current += 1;

        if (!isTokenExpired() && consecutiveAuthFailures.current <= 2) {
          const newToken = await refreshToken();
          if (newToken) {
            consecutiveAuthFailures.current = 0;
            await fetchStats(force);
            return;
          }
        }

        if (consecutiveAuthFailures.current >= 3) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
        setError('Authentication failed. Please log in again.');
        setLoading(false);
        if (!statsRef.current) {
          setStats(DEFAULT_STATS);
        }
        return;
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to fetch statistics');
      }

      consecutiveAuthFailures.current = 0;

      const data = await response.json();

      if (data.success && data.stats) {
        setStats(data.stats);
        setIsCached(data.stats.is_cached || false);
        setLastUpdated(new Date());
        setError(null);
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      if (!mountedRef.current) return;

      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch statistics';
      if (!errorMessage.includes('401') && !errorMessage.includes('Unauthorized')) {
        setError(errorMessage);
      }

      if (!statsRef.current) {
        setStats(DEFAULT_STATS);
      }
    } finally {
      isFetchingRef.current = false;
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [enabled, getAuthHeaders, getUserId, maxAge]);

  const fetchStatsRef = useRef(fetchStats);
  fetchStatsRef.current = fetchStats;

  const refresh = useCallback(async (force = false) => {
    if (!isAuthenticated()) {
      return;
    }
    setLoading(true);
    consecutiveAuthFailures.current = 0;
    await fetchStats(force);
  }, [fetchStats, isAuthenticated]);

  useEffect(() => {
    mountedRef.current = true;
    consecutiveAuthFailures.current = 0;

    const authenticated = isAuthenticated();

    if (!enabled || !authenticated) {
      setLoading(false);
      setStats(DEFAULT_STATS);
      return;
    }

    fetchStatsRef.current();

    if (autoRefresh && refreshInterval > 0) {
      intervalRef.current = window.setInterval(() => {
        if (consecutiveAuthFailures.current < 3 && isAuthenticated()) {
          fetchStatsRef.current();
        }
      }, refreshInterval);
    }

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, autoRefresh, refreshInterval, isAuthenticated]);

  useEffect(() => {
    if (!enabled || !isAuthenticated()) {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      setStats(DEFAULT_STATS);
      return;
    }

    const adminId = getAdminId();
    if (!adminId) return;

    channelRef.current = supabase
      .channel(`order-stats-${adminId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'shopify_order_stats_cache',
          filter: `admin_id=eq.${adminId}`,
        },
        (payload) => {
          if (!mountedRef.current) return;

          if (payload.new && typeof payload.new === 'object') {
            const newStats = payload.new as AggregateOrderStats & { admin_id: string };
            setStats({
              total_orders: newStats.total_orders,
              total_revenue: newStats.total_revenue,
              pending_orders: newStats.pending_orders,
              fulfilled_orders: newStats.fulfilled_orders,
              ready_to_ship: newStats.ready_to_ship,
              to_prepare: newStats.to_prepare,
              with_tracking: newStats.with_tracking,
              today_orders: newStats.today_orders,
              today_revenue: newStats.today_revenue,
              printed_today: newStats.printed_today,
              avg_order_value: newStats.avg_order_value,
              fulfillment_rate: newStats.fulfillment_rate,
              primary_currency: newStats.primary_currency || 'EUR',
              last_order_date: newStats.last_order_date,
              oldest_order_date: newStats.oldest_order_date,
              calculated_at: newStats.calculated_at,
              orders_with_missing_items: newStats.orders_with_missing_items || 0,
              is_cached: true,
            });
            setLastUpdated(new Date());
            setIsCached(true);
          }
        }
      )
      .subscribe();

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [enabled, isAuthenticated, getUserId]);

  return {
    stats,
    loading,
    error,
    lastUpdated,
    isCached,
    refresh,
  };
}

export function formatStatNumber(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toLocaleString();
}

export function formatStatCurrency(value: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}
